/**
 * TransmittalDetail — paper Form F-1.7.0 layout + lifecycle actions
 * for AISC §1.7.0 Phase 3.
 */

import React, { useEffect, useState } from "react";
import { prettyStatus } from "../../components/PaperForm/styles";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router-dom";
import {
  Container, Typography, Stack, Button, Paper, Alert, Chip, Divider,
  TextField, Breadcrumbs, Link as MuiLink, Box, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TableHead, TableBody, TableRow, TableCell,
} from "@mui/material";
import {
  HomeRounded, ArrowBackRounded, EditRounded, SendRounded,
  ReplyRounded, CheckCircleOutlineRounded, CancelRounded,
} from "@mui/icons-material";

import {
  getTransmittal, sendTransmittal, returnTransmittal, closeTransmittal,
  cancelTransmittal, allowedTransmittalTransitions, ALLOWED_RETURN_ACTIONS,
} from "../../utils/transmittalsApi";

import SmartTable from "../../components/SmartTable/SmartTable";
const STATUS_COLOR = {
  DRAFT:     { bg: "#e9ecef", color: "#6c757d" },
  SENT:      { bg: "#cfe2ff", color: "#052c65" },
  RETURNED:  { bg: "#fff3cd", color: "#664d03" },
  CLOSED:    { bg: "#198754", color: "#ffffff" },
  CANCELLED: { bg: "#f8d7da", color: "#58151c" },
};

const ACTION_LABELS = {
  APPROVAL: "For approval",
  APPROVED_AS_SUBMITTED: "Approved as submitted",
  APPROVED_AS_NOTED: "Approved as noted",
  YOUR_USE: "For your use",
  AS_REQUIRED: "As required",
  RETURNED_AFTER_LOAN: "Returned after loan",
  REVIEW_AND_COMMENT: "Review and comment",
  RESUBMIT: "Resubmit",
  SUBMIT: "Submit",
  RETURNED: "Returned",
  CHANGE_ORDER: "Change order",
};

export default function TransmittalDetail() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [tr, setTr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const [openReturn, setOpenReturn] = useState(false);
  const [returnAction, setReturnAction] = useState("APPROVED_AS_SUBMITTED");
  const [returnNotes, setReturnNotes] = useState("");
  const [returnDocId, setReturnDocId] = useState("");

  const [openCancel, setOpenCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  const refresh = () => {
    setLoading(true);
    getTransmittal(id)
      .then(setTr)
      .catch((err) => setError(err?.response?.data?.error || err?.message))
      .finally(() => setLoading(false));
  };
  useEffect(refresh, [id]);

  const fireAction = async (fn, ...args) => {
    setBusy(true); setError(null);
    try { await fn(id, ...args); refresh(); }
    catch (err) { setError(err?.response?.data?.error || err?.message); }
    finally { setBusy(false); }
  };

  const handleReturn = async () => {
    if (!returnAction) {
      setError("Return action required");
      return;
    }
    setBusy(true); setError(null);
    try {
      await returnTransmittal(id, {
        returned_with_action: returnAction,
        returned_notes: returnNotes || null,
        returned_doc_id: returnDocId ? Number(returnDocId) : null,
      });
      setOpenReturn(false); setReturnNotes(""); setReturnDocId("");
      refresh();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message);
    } finally { setBusy(false); }
  };

  if (loading) return <Container sx={{ py: 4 }}><Typography>Loading…</Typography></Container>;
  if (!tr) return <Container sx={{ py: 4 }}><Alert severity="error">Transmittal not found.</Alert></Container>;

  const colors = STATUS_COLOR[tr.status] || { bg: "#e9ecef", color: "#495057" };
  const next = allowedTransmittalTransitions(tr.status);
  const items = tr.items || [];

  return (
    <>
      <Helmet><title>{tr.transmittal_number} | SFM</title></Helmet>
      <Container maxWidth="md" sx={{ py: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <MuiLink href="/" color="inherit" underline="hover">
            <Stack direction="row" alignItems="center" spacing={0.5}><HomeRounded fontSize="small" /> <span>Home</span></Stack>
          </MuiLink>
          <MuiLink href="/transmittals" color="inherit" underline="hover">Transmittals</MuiLink>
          <Typography color="text.primary">{tr.transmittal_number}</Typography>
        </Breadcrumbs>

        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Button startIcon={<ArrowBackRounded />} onClick={() => navigate("/transmittals")}>Back</Button>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>{tr.transmittal_number}</Typography>
            <Chip label={prettyStatus(tr.status)} sx={{ backgroundColor: colors.bg, color: colors.color, fontWeight: 600 }} />
          </Stack>
          <Stack direction="row" spacing={1}>
            {tr.status === "DRAFT" && (
              <Button startIcon={<EditRounded />} onClick={() => navigate(`/transmittals/${id}/edit`)}>Edit</Button>
            )}
            {next.includes("SENT") && (
              <Button startIcon={<SendRounded />} variant="contained" disabled={busy}
                onClick={() => fireAction(sendTransmittal)}>Send</Button>
            )}
            {next.includes("RETURNED") && (
              <Button startIcon={<ReplyRounded />} variant="contained" color="success" disabled={busy}
                onClick={() => setOpenReturn(true)}>Record return</Button>
            )}
            {next.includes("CLOSED") && (
              <Button startIcon={<CheckCircleOutlineRounded />} variant="contained" disabled={busy}
                onClick={() => fireAction(closeTransmittal)}>Close</Button>
            )}
            {next.includes("CANCELLED") && (
              <Button startIcon={<CancelRounded />} color="error" disabled={busy}
                onClick={() => setOpenCancel(true)}>Cancel</Button>
            )}
          </Stack>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Paper sx={{ p: 3 }}>
          {/* Paper-form layout — Form F-1.7.0 mirror */}
          <Box sx={{ borderBottom: "1px solid #dee2e6", pb: 2, mb: 2 }}>
            <Typography variant="caption" sx={{ color: "#6c757d", letterSpacing: 1 }}>
              FORM F-1.7.0 / TRANSMITTAL
            </Typography>
            <Typography variant="h6">{tr.transmittal_number}</Typography>
            <Typography variant="body2" sx={{ color: "#6c757d" }}>
              Job #{tr.job_id} · Created {tr.created_at ? new Date(tr.created_at).toISOString().slice(0, 10) : "—"}
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ color: "#6c757d" }}>Recipient</Typography>
            <Typography>{tr.recipient_party}</Typography>
            {tr.recipient_address && (
              <Typography variant="body2" sx={{ color: "#6c757d", whiteSpace: "pre-wrap" }}>
                {tr.recipient_address}
              </Typography>
            )}
            {tr.recipient_email && (
              <Typography variant="body2" sx={{ color: "#6c757d" }}>{tr.recipient_email}</Typography>
            )}
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ color: "#6c757d" }}>Sent via</Typography>
            <Typography>
              {tr.sent_via}
              {tr.sent_via === "OTHER" && tr.sent_via_other_text && ` — ${tr.sent_via_other_text}`}
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ color: "#6c757d" }}>Action requested</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
              {(tr.action_requested || []).map((a) => (
                <Chip key={a} size="small" label={ACTION_LABELS[a] || a}
                  sx={{ mb: 0.5, backgroundColor: "#e7f1ff", color: "#052c65" }} />
              ))}
            </Stack>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ color: "#6c757d", mb: 1 }}>
              Items ({items.length})
            </Typography>
            <SmartTable tableId="transmittal-items" lockedColumns={["Type", "Doc ID"]} size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Doc ID</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Copies</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!items.length && (
                  <TableRow><TableCell colSpan={4} sx={{ color: "#6c757d" }}>
                    No items on this transmittal.
                  </TableCell></TableRow>
                )}
                {items.map((it) => (
                  <TableRow key={it.id}>
                    <TableCell><Typography variant="caption">{it.item_type}</Typography></TableCell>
                    <TableCell>{it.document_id || "—"}</TableCell>
                    <TableCell>{it.item_description || "—"}</TableCell>
                    <TableCell align="right">{it.copies}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </SmartTable>
          </Box>

          {tr.notes && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ color: "#6c757d" }}>Notes</Typography>
              <Typography sx={{ whiteSpace: "pre-wrap", py: 1 }}>{tr.notes}</Typography>
            </Box>
          )}

          {tr.returned_at && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ color: "#0a3622", fontWeight: 600 }}>
                  Returned {new Date(tr.returned_at).toISOString().slice(0, 10)}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  <strong>Action:</strong> {ACTION_LABELS[tr.returned_with_action] || tr.returned_with_action}
                </Typography>
                {tr.returned_doc_id && (
                  <Typography variant="body2" sx={{ color: "#6c757d" }}>
                    Returned doc: #{tr.returned_doc_id}
                  </Typography>
                )}
                {tr.returned_notes && (
                  <Typography sx={{ whiteSpace: "pre-wrap", py: 1 }}>{tr.returned_notes}</Typography>
                )}
              </Box>
            </>
          )}

          {tr.cancellation_reason && (
            <>
              <Divider sx={{ my: 2 }} />
              <Alert severity="error">
                <Typography variant="body2"><strong>Cancelled:</strong> {tr.cancellation_reason}</Typography>
              </Alert>
            </>
          )}

          <Divider sx={{ my: 2 }} />
          <Box sx={{ color: "#6c757d", fontSize: "0.8rem" }}>
            <Typography variant="caption" component="div">
              {tr.sent_at && <>Sent {new Date(tr.sent_at).toISOString().slice(0, 19).replace("T", " ")} UTC. </>}
              Records preserved per AISC §1.7.0.
            </Typography>
          </Box>
        </Paper>

        <Box sx={{ mt: 3, color: "#6c757d", fontSize: "0.8rem" }}>
          <Typography variant="caption">
            Tip: Use Cmd+P / Ctrl+P to print this page as PDF (interim until Phase 4 server-side PDF lands).
          </Typography>
        </Box>
      </Container>

      <Dialog open={openReturn} onClose={() => setOpenReturn(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record transmittal return</DialogTitle>
        <DialogContent>
          <TextField select fullWidth label="Returned with action" required
            value={returnAction} onChange={(e) => setReturnAction(e.target.value)} sx={{ mb: 2, mt: 1 }}>
            {ALLOWED_RETURN_ACTIONS.map((a) => (
              <MenuItem key={a} value={a}>{ACTION_LABELS[a] || a.replace(/_/g, " ")}</MenuItem>
            ))}
          </TextField>
          <TextField fullWidth label="Returned doc ID (optional)" type="number"
            value={returnDocId} onChange={(e) => setReturnDocId(e.target.value)} sx={{ mb: 2 }}
            helperText="If a marked-up or stamped doc was returned, link it here." />
          <TextField fullWidth multiline rows={4} label="Return notes" value={returnNotes}
            onChange={(e) => setReturnNotes(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenReturn(false)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={handleReturn} disabled={busy}>
            {busy ? "Saving…" : "Record return"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openCancel} onClose={() => setOpenCancel(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cancel transmittal</DialogTitle>
        <DialogContent>
          <TextField fullWidth multiline rows={3} label="Cancellation reason (min 4 chars)" required
            value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCancel(false)} disabled={busy}>Back</Button>
          <Button variant="contained" color="error" onClick={async () => {
            await fireAction(cancelTransmittal, cancelReason);
            setOpenCancel(false); setCancelReason("");
          }} disabled={busy || cancelReason.length < 4}>Cancel transmittal</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
